import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Save, Loader2, Building2, Mail, Send, ShieldCheck, ShieldAlert, Copy, Key, Trash2, AlertTriangle,
} from "lucide-react";
import { adminApi } from "../../lib/api";
import { authClient } from "../../lib/AuthContext";

const Field = ({ label, value, onChange, placeholder, testid, type = "text" }) => (
  <label className="block">
    <span className="text-xs uppercase tracking-widest font-display text-[#3f4b52]">{label}</span>
    <input
      type={type}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      data-testid={testid}
      className="mt-1 w-full h-10 px-3 border border-[#cfd7db] rounded-md text-sm bg-white focus:outline-none focus:border-[#f28a16]"
    />
  </label>
);

const Section = ({ icon: Icon, title, subtitle, children }) => (
  <section className="bg-white border border-[#cfd7db] rounded-md">
    <header className="px-5 py-4 border-b border-[#cfd7db] flex items-start gap-3">
      <div className="w-10 h-10 rounded-md bg-[#f28a16]/10 text-[#f28a16] flex items-center justify-center flex-shrink-0">
        <Icon size={18} />
      </div>
      <div>
        <h2 className="font-display uppercase tracking-widest text-sm text-[#111619]">{title}</h2>
        {subtitle && <p className="text-xs text-[#3f4b52] mt-0.5">{subtitle}</p>}
      </div>
    </header>
    <div className="p-5">{children}</div>
  </section>
);

const AdminSettings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [sending, setSending] = useState(false);
  const [apiKeyInput, setApiKeyInput] = useState("");
  const [wipeText, setWipeText] = useState("");
  const [wipeBusy, setWipeBusy] = useState(false);
  const [wipeResult, setWipeResult] = useState(null);

  const doWipe = async () => {
    if (wipeText.trim().toUpperCase() !== "WIPE") return toast.error('Please type "WIPE" exactly to confirm');
    if (!window.confirm("Absolutely sure? This deletes every job, customer, invoice, booking, quote, and all other admins. Only YOUR account survives. Irreversible.")) return;
    setWipeBusy(true);
    try {
      const r = await authClient.post("/admin/wipe-demo-data");
      setWipeResult(r.data?.summary || {});
      toast.success("Demo data wiped — you're the only user left.");
      setWipeText("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Wipe failed");
    } finally { setWipeBusy(false); }
  };

  const load = async () => {
    setLoading(true);
    try { setSettings(await adminApi.getSettings()); }
    catch { toast.error("Could not load settings"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const patchCompany = (k, v) => setSettings((s) => ({ ...s, company: { ...s.company, [k]: v } }));
  const patchBank    = (k, v) => setSettings((s) => ({ ...s, company: { ...s.company, bank: { ...s.company.bank, [k]: v } } }));
  const patchEmail   = (k, v) => setSettings((s) => ({ ...s, email: { ...s.email, [k]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        company: settings.company,
        email: {
          sender_email: settings.email.sender_email,
          sender_name: settings.email.sender_name,
          reply_to: settings.email.reply_to,
        },
      };
      if (apiKeyInput.trim()) payload.email.api_key = apiKeyInput.trim();
      const updated = await adminApi.updateSettings(payload);
      setSettings(updated);
      setApiKeyInput("");
      toast.success("Settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  const sendTest = async () => {
    if (!testTo) return;
    setSending(true);
    try {
      const r = await adminApi.sendTestEmail(testTo);
      if (r.ok) {
        if (r.used_sandbox) {
          toast.warning(
            `Sent from Resend sandbox (onboarding@resend.dev). Verify your domain at resend.com/domains to send from ${settings.email.sender_email}.`,
            { duration: 10000 },
          );
        } else {
          toast.success(`Sent — Resend id ${r.id?.slice(0, 8) || "ok"}`);
        }
        setSettings((s) => ({ ...s, email: { ...s.email, verified: true, used_sandbox: r.used_sandbox } }));
      } else {
        const message = r.hint || r.error || "Send failed";
        toast.error(message, { duration: 12000 });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setSending(false); }
  };

  if (loading || !settings) return (
    <div className="p-10 text-center text-[#3f4b52]" data-testid="admin-settings-loading">
      <Loader2 className="animate-spin inline-block mr-2" size={16} /> Loading settings…
    </div>
  );

  const c = settings.company;
  const e = settings.email;

  return (
    <div className="max-w-4xl" data-testid="admin-settings-page">
      <h1 className="font-display font-black uppercase text-3xl sm:text-4xl text-[#111619]">Settings</h1>
      <p className="text-[#3f4b52] mt-1">
        Company details on every PDF · Resend email credentials. Any change here is picked up by the next generated document — no redeploy needed.
      </p>

      <div className="mt-6 space-y-6">
        <Section icon={Building2} title="Company Details" subtitle="These print on quotes, invoices, credit notes, statements and the claim form.">
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Legal name"   value={c.legal_name}   onChange={(v) => patchCompany("legal_name", v)}   testid="s-legal-name" />
            <Field label="Trading name" value={c.trading_name} onChange={(v) => patchCompany("trading_name", v)} testid="s-trading-name" />
            <Field label="Company reg no." value={c.reg_number} onChange={(v) => patchCompany("reg_number", v)}  testid="s-reg-number" />
            <Field label="VAT no."      value={c.vat_number}   onChange={(v) => patchCompany("vat_number", v)}   testid="s-vat-number" />
            <Field label="Address line 1" value={c.address_line1} onChange={(v) => patchCompany("address_line1", v)} testid="s-address1" />
            <Field label="Address line 2" value={c.address_line2} onChange={(v) => patchCompany("address_line2", v)} testid="s-address2" />
            <Field label="Postal code"  value={c.postal_code}  onChange={(v) => patchCompany("postal_code", v)}  testid="s-postal" />
            <Field label="Cell number"  value={c.cell}         onChange={(v) => patchCompany("cell", v)}         testid="s-cell" />
            <Field label="Website"      value={c.website || ""} onChange={(v) => patchCompany("website", v)}      testid="s-website" placeholder="www.dieseldustgroup.co.za" />
            <Field label="Contact email" value={c.email}       onChange={(v) => patchCompany("email", v)}        testid="s-company-email" />
            <Field label="Tagline"      value={c.tagline || ""} onChange={(v) => patchCompany("tagline", v)}     testid="s-tagline" />
          </div>
        </Section>

        <Section icon={Building2} title="Banking Details" subtitle="Printed as the payment footer on every invoice and statement.">
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Bank"            value={c.bank?.name || ""}           onChange={(v) => patchBank("name", v)}           testid="s-bank-name" />
            <Field label="Account holder"  value={c.bank?.account_holder || ""} onChange={(v) => patchBank("account_holder", v)} testid="s-bank-holder" />
            <Field label="Account number"  value={c.bank?.account_number || ""} onChange={(v) => patchBank("account_number", v)} testid="s-bank-number" />
            <Field label="Branch code"     value={c.bank?.branch_code || ""}    onChange={(v) => patchBank("branch_code", v)}    testid="s-bank-branch" />
            <Field label="Account type"    value={c.bank?.account_type || ""}   onChange={(v) => patchBank("account_type", v)}   testid="s-bank-type" />
          </div>
        </Section>

        <Section icon={Mail} title="Email (Resend)" subtitle="Used for magic-link emails and future 'Bundle & Send' insurance packs.">
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Sender email" value={e.sender_email || ""} onChange={(v) => patchEmail("sender_email", v)} placeholder="admin@dieseldustgroup.co.za" testid="s-email-sender" />
            <Field label="Sender name"  value={e.sender_name || ""}  onChange={(v) => patchEmail("sender_name", v)}  placeholder="Diesel & Dust" testid="s-email-sender-name" />
            <Field label="Reply-to"     value={e.reply_to || ""}     onChange={(v) => patchEmail("reply_to", v)}     placeholder="admin@dieseldustgroup.co.za" testid="s-email-replyto" />
          </div>

          <div className="mt-6 pt-6 border-t border-[#cfd7db]">
            <div className="flex items-center gap-3 flex-wrap">
              <Key size={16} className="text-[#3f4b52]" />
              <div className="text-sm font-semibold text-[#111619]">Resend API key</div>
              {e.api_key_set ? (
                <span className="inline-flex items-center gap-1 text-xs bg-[#10b981]/10 text-[#065f46] px-2 py-0.5 rounded font-display uppercase tracking-widest" data-testid="s-key-status">
                  <ShieldCheck size={12} /> Saved · ends ····{e.api_key_last4}
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-xs bg-[#dc2626]/10 text-[#7f1d1d] px-2 py-0.5 rounded font-display uppercase tracking-widest" data-testid="s-key-status">
                  <ShieldAlert size={12} /> Not configured
                </span>
              )}
            </div>
            {e.api_key_set && (
              <div className="mt-2 bg-[#f0fdf4] border border-[#bbf7d0] text-[#065f46] rounded-md p-3 text-xs" data-testid="s-key-saved-banner">
                <b>Your API key is stored securely.</b> The field below stays blank on every load so the raw key can&apos;t be read from your screen. To keep the current key, just leave it empty and click Save. To replace it, paste a new one and click Save.
              </div>
            )}
            <p className="text-xs text-[#3f4b52] mt-2">
              Paste your Resend API key below (starts with <code className="bg-[#f5f6f6] px-1">re_</code>). Leave blank to keep the current key. Get one at{" "}
              <a href="https://resend.com/api-keys" target="_blank" rel="noreferrer" className="text-[#f28a16] hover:underline">resend.com/api-keys</a>.
            </p>
            <input
              type="password"
              value={apiKeyInput}
              onChange={(ev) => setApiKeyInput(ev.target.value)}
              placeholder={e.api_key_set ? "•••••••••• (leave blank to keep)" : "re_… (starts with re_, at least 20 characters)"}
              data-testid="s-api-key-input"
              className="mt-2 w-full h-10 px-3 border border-[#cfd7db] rounded-md text-sm bg-white font-mono focus:outline-none focus:border-[#f28a16]"
            />
          </div>

          {e.api_key_set && (
            <div className="mt-6 bg-[#fff7ed] border border-[#fed7aa] text-[#7c2d12] rounded-md p-3 text-xs" data-testid="s-domain-note">
              <b>Note about your sender email.</b> Resend requires you to verify <code>{(e.sender_email || "").split("@")[1] || "your domain"}</code> before you can send from{" "}
              <b>{e.sender_email}</b>. Until then, we&apos;ll transparently send from Resend&apos;s shared sandbox address <code>onboarding@resend.dev</code> so your tests still work.
              To send fully-branded emails from your own domain, add the DNS records at{" "}
              <a href="https://resend.com/domains" target="_blank" rel="noreferrer" className="underline">resend.com/domains</a>.
            </div>
          )}

          <div className="mt-6 pt-6 border-t border-[#cfd7db]">
            <div className="text-sm font-semibold text-[#111619] mb-1">Send a test email</div>
            <p className="text-xs text-[#3f4b52] mb-3">Save the key first, then send yourself a test to confirm delivery.</p>
            <div className="flex gap-2 flex-wrap">
              <input
                type="email"
                value={testTo}
                onChange={(ev) => setTestTo(ev.target.value)}
                placeholder="you@example.com"
                data-testid="s-test-recipient"
                className="flex-1 min-w-[240px] h-10 px-3 border border-[#cfd7db] rounded-md text-sm bg-white"
              />
              <button
                onClick={sendTest}
                disabled={sending || !testTo || !e.api_key_set}
                data-testid="s-test-send-btn"
                className="bg-[#20282c] text-white px-4 h-10 rounded-md font-display uppercase tracking-widest text-xs inline-flex items-center gap-2 disabled:opacity-40"
              >
                {sending ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />}
                Send test
              </button>
            </div>
            {e.verified && (
              <div className="mt-2 text-xs inline-flex items-center gap-1 text-[#065f46]" data-testid="s-verified-badge">
                <ShieldCheck size={12} /> Verified — a test email has been sent successfully.
              </div>
            )}
          </div>
        </Section>

        <div className="sticky bottom-0 bg-[#f5f6f6]/95 backdrop-blur border-t border-[#cfd7db] -mx-6 px-6 py-4 flex justify-end">
          <button
            onClick={save}
            disabled={saving}
            data-testid="s-save-btn"
            className="bg-[#f28a16] text-white px-6 h-11 rounded-md font-display uppercase tracking-widest text-xs inline-flex items-center gap-2 hover:brightness-110 disabled:opacity-60"
          >
            {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}
            Save changes
          </button>
        </div>
      </div>

      {/* Danger Zone — clears preview/seed data from production. Manager-only. */}
      <section className="bg-white border-2 border-[#dc2626]/40 rounded-md mt-8" data-testid="danger-zone">
        <header className="px-5 py-4 border-b border-[#dc2626]/40 flex items-start gap-3 bg-[#fef2f2]">
          <div className="w-10 h-10 rounded-md bg-[#dc2626]/10 text-[#dc2626] flex items-center justify-center flex-shrink-0">
            <AlertTriangle size={18} />
          </div>
          <div>
            <h2 className="font-display uppercase tracking-widest text-sm text-[#7f1d1d]">Danger Zone — Wipe demo data</h2>
            <p className="text-xs text-[#7f1d1d]/80 mt-0.5">
              Removes every job, customer, invoice, booking, quote, credit note, testimonial, notification, audit-log entry, backup record AND every other user — leaving only YOUR account. Meant for first-time production cleanup after the deploy pipeline seeded test data. <b>Irreversible.</b>
            </p>
          </div>
        </header>
        <div className="p-5 space-y-3">
          <label className="block max-w-md">
            <span className="text-xs uppercase tracking-widest font-display text-[#7f1d1d]">Type <b>WIPE</b> to enable the button</span>
            <input
              type="text"
              value={wipeText}
              onChange={(e) => setWipeText(e.target.value)}
              placeholder="WIPE"
              data-testid="wipe-confirm-input"
              className="mt-1 w-full h-10 px-3 border border-[#dc2626]/40 rounded-md text-sm bg-white focus:outline-none focus:border-[#dc2626] uppercase tracking-widest font-mono"
            />
          </label>
          <button
            onClick={doWipe}
            disabled={wipeBusy || wipeText.trim().toUpperCase() !== "WIPE"}
            data-testid="wipe-btn"
            className="bg-[#dc2626] text-white px-5 h-11 rounded-md font-display uppercase tracking-widest text-xs inline-flex items-center gap-2 hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {wipeBusy ? <Loader2 className="animate-spin" size={14} /> : <Trash2 size={14} />}
            Wipe all demo data
          </button>
          {wipeResult && (
            <div className="mt-3 rounded-md bg-[#f0fdf4] border border-[#10b981]/40 p-3 text-xs" data-testid="wipe-result">
              <div className="font-display uppercase tracking-widest text-[#065f46] mb-1">Wipe complete — collections cleared:</div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-[#065f46]/80 font-mono">
                {Object.entries(wipeResult).filter(([_, n]) => n > 0).map(([k, n]) => (
                  <div key={k}>{k}: <b>{n}</b></div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default AdminSettings;
